/**
 * Reverb and delay for the chord-bar players, saved on the song as `chartEffects`.
 *
 * One reverb and one delay per play, each player sending into them from its own gain — the way a
 * mixing desk does it, and two convolvers rather than one per voice. Stored as 0..1 per player and
 * send, e.g. `{ piano: { reverb: 0.2 }, guitar: { delay: 0.3 } }`. Absent plays everything dry.
 */

// The players that send, in the mixer's order. The bass is left out on purpose: reverb on the low
// end muddies the whole band.
export const SEND_PLAYERS = [
  { id: 'piano', name: 'Instrument', delay: false },
  { id: 'drums', name: 'Drums', delay: false, reverbScale: 0.12 },
  { id: 'pad', name: 'Pad', delay: false },
  { id: 'brass', name: 'Brass', delay: false },
  { id: 'guitar', name: 'Guitar', delay: true },
  { id: 'acoustic', name: 'Acoustic', delay: false },
  { id: 'violin', name: 'Violin', delay: true },
  { id: 'melody', name: 'Melody', delay: true },
];

export const REVERB = 'reverb';
export const DELAY = 'delay';

const REVERB_OPTIONS = { decay: 3, preDelay: 0.02, wet: 1 };
// The convolver normalizes its impulse, which leaves the reverb about 8 dB under what was sent to it,
// masked under held chords. The return brings it back so a full send sits about level with the player.
const REVERB_RETURN_DB = 8;
const DELAY_OPTIONS = { delayTime: '8n.', feedback: 0.3, wet: 1 };
// The echoes lose their low end, so they don't build up under the band
const DELAY_HIGHPASS = 300;
const RAMP = 0.05;

const findPlayer = (id) => SEND_PLAYERS.find((player) => player.id === id);

/** The gain a player sends to a bus: the stored 0..1, scaled for the drums, 0 for a send it doesn't have. */
export const getSendGain = (effects, id, bus) => {
  const player = findPlayer(id);

  if (!player || (bus === DELAY && !player.delay)) return 0;

  const amount = Math.min(Math.max(Number(effects?.[id]?.[bus]) || 0, 0), 1);

  return bus === REVERB ? amount * (player.reverbScale ?? 1) : amount;
};

export const hasEffects = (effects) =>
  SEND_PLAYERS.some(({ id }) => getSendGain(effects, id, REVERB) > 0 || getSendGain(effects, id, DELAY) > 0);

/** The stored value with one send changed. A send at 0 is dropped, and null is left once none are. */
export const setSend = (effects, id, bus, value) => {
  const next = { ...(effects || {}) };
  const player = { ...next[id] };

  if (value > 0) player[bus] = Math.round(Math.min(value, 1) * 100) / 100;
  else delete player[bus];

  if (Object.keys(player).length > 0) next[id] = player;
  else delete next[id];

  return Object.keys(next).length > 0 ? next : null;
};

/**
 * The two buses, into `destination`. Built on every play even with nothing sent, so a send turned up
 * while playing is heard at once; a send at 0 passes nothing, so a dry song sounds as it did.
 */
export const createSendBuses = async (Tone, destination, effects) => {
  const reverbReturn = new Tone.Volume(REVERB_RETURN_DB).connect(destination);
  const reverb = new Tone.Reverb(REVERB_OPTIONS).connect(reverbReturn);
  const highpass = new Tone.Filter(DELAY_HIGHPASS, 'highpass').connect(destination);
  const delay = new Tone.FeedbackDelay(DELAY_OPTIONS).connect(highpass);

  // Notes before the impulse response lands come out dry (see createEffectChain), so it is waited on
  // where something is sent to it
  if (SEND_PLAYERS.some(({ id }) => getSendGain(effects, id, REVERB) > 0)) await reverb.ready;

  return { reverb, delay, nodes: [reverb, reverbReturn, delay, highpass] };
};

/** A player's sends, from its own gain into each bus it has. Null for a player that doesn't send. */
export const connectSends = (Tone, source, buses, id, effects) => {
  const player = findPlayer(id);

  if (!player || !buses) return null;

  const send = (bus) => {
    const gain = new Tone.Gain(getSendGain(effects, id, bus)).connect(buses[bus]);

    source.connect(gain);

    return gain;
  };

  return { reverb: send(REVERB), delay: player.delay ? send(DELAY) : null };
};

// Ramped rather than set, like the levels: a jump on a sounding note is a click of its own
export const rampSends = (sends, id, effects) => {
  sends.reverb.gain.rampTo(getSendGain(effects, id, REVERB), RAMP);
  if (sends.delay) sends.delay.gain.rampTo(getSendGain(effects, id, DELAY), RAMP);
};

export const disposeSends = (sends) => {
  sends.reverb.dispose();
  if (sends.delay) sends.delay.dispose();
};
